<template>
    <b-overlay :show="loading" rounded="sm">
        <b-card>
            <div class="mb-1">
                <b-row>
                    <b-col cols="12">
                        <h6 class="text-muted">Transfer Bilgileri</h6>
                        <hr class="mt-0" />
                    </b-col>

                    <b-col md="3">
                        <b-form-group label="İşlem No" class="is-required">
                            <v-validator :errors="errors" parameter="ProcessNumber" :watch="transferModel.ProcessNumber">
                                <b-form-input v-model="transferModel.ProcessNumber" placeholder="İşlem Numarası" disabled />
                            </v-validator>
                        </b-form-group>
                    </b-col>

                    <b-col md="2">
                        <b-form-group label="Yakıt Tipi" class="is-required">
                            <v-validator :errors="errors" parameter="selectedFuelTypeId" :watch="selectedFuelTypeId">
                                <v-select-list v-model="selectedFuelTypeId" :options="options.fuelTypes" label="text" :reduce="(t) => t.value" placeholder="Seçiniz" />
                            </v-validator>
                        </b-form-group>
                    </b-col>

                    <b-col md="3">
                        <b-form-group label="Sürücü" class="is-required">
                            <v-validator :errors="errors" parameter="CarrierEmployeeId" :watch="transferModel.CarrierEmployeeId">
                                <v-select-list v-model="transferModel.CarrierEmployeeId" :options="options.employees" label="FullName" :reduce="(emp) => emp.Id" placeholder="İsim yazarak arayın..." @search="searchEmployees" :loading="employeeLoading" />
                            </v-validator>
                        </b-form-group>
                    </b-col>

                    <b-col md="2">
                        <b-form-group label="Araç Plaka" class="is-required">
                            <v-validator :errors="errors" parameter="VehiclePlateId" :watch="transferModel.VehiclePlateId">
                                <v-select-list v-model="transferModel.VehiclePlateId" :options="options.truckPlates" label="PlateValue" :reduce="(plate) => plate.Id" placeholder="Araç Seçiniz" />
                            </v-validator>
                        </b-form-group>
                    </b-col>

                    <b-col md="2">
                        <b-form-group label="Treyler" class="is-required">
                            <v-validator :errors="errors" parameter="TrailerPlateId" :watch="transferModel.TrailerPlateId">
                                <v-select-list v-model="transferModel.TrailerPlateId" :options="options.trailerPlates" label="PlateValue" :reduce="(plate) => plate.Id" placeholder="Treyler Seçiniz" />
                            </v-validator>
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>

            <b-row class="align-items-stretch">
                <b-col md="6" class="d-flex">
                    <b-card no-body class="w-100 d-flex flex-column border-danger">
                        <b-card-header class="bg-transparent border-bottom py-75 px-1">
                            <div class="d-flex justify-content-between align-items-center w-100">
                                <h5 class="mb-0 text-danger"><feather-icon size="20" icon="ArrowUpCircleIcon" /> Kaynak (Çıkış)</h5>
                                <b-button variant="outline-danger" size="sm" class="btn-icon rounded-circle" @click="addSourceRow">
                                    <feather-icon icon="PlusIcon" />
                                </b-button>
                            </div>
                        </b-card-header>

                        <b-card-body class="px-1 pt-2 pb-0 flex-grow-1">
                            <div v-for="(item, index) in transferModel.FromFuelStorages" :key="'src-' + index" class="mb-1 p-1 border rounded bg-light">
                                <b-row class="mb-50">
                                    <b-col cols="12">
                                        <b-form-group label="Depo" label-class="font-weight-bold font-small-2 mb-0" class="mb-0 is-required">
                                            <div class="d-flex">
                                                <div class="flex-grow-1">
                                                    <v-validator :errors="errors" :parameter="'FromFuelStorages.' + index + '.FromFuelStorageId'" :watch="item.FromFuelStorageId">
                                                        <v-select-list
                                                            v-model="item.FromFuelStorageId"
                                                            :options="filteredFuelStorages"
                                                            label="DisplayName"
                                                            :reduce="(s) => s.Id"
                                                            :placeholder="selectedFuelTypeId ? 'Kaynak Depo Seçiniz' : 'Lütfen önce yakıt tipi seçiniz...'"
                                                            :disabled="!selectedFuelTypeId"
                                                            @input="handleStorageChange($event, item, 'source', index)"
                                                        />
                                                    </v-validator>
                                                </div>
                                                <b-button variant="flat-danger" size="sm" class="btn-icon ml-1" @click="removeSourceRow(index)" v-if="transferModel.FromFuelStorages.length > 1" style="height: 38px">
                                                    <feather-icon icon="TrashIcon" />
                                                </b-button>
                                            </div>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-row class="mb-50">
                                    <b-col cols="12">
                                        <div style="min-height: 26px" class="d-flex align-items-center">
                                            <div v-if="getStorageInfo(item.FromFuelStorageId)" class="d-flex align-items-center font-small-3 w-100">
                                                <span class="font-weight-bold text-primary mr-1">Depodaki Mevcut Yakıt:</span>
                                                <b-badge variant="light-primary" class="font-small-3"> {{ formatNumber(getStorageInfo(item.FromFuelStorageId).FuelQuantity) }} lt </b-badge>
                                            </div>
                                        </div>
                                    </b-col>
                                </b-row>

                                <b-row>
                                    <b-col md="4">
                                        <b-form-group label="Yoğunluk" label-class="font-weight-bold font-small-2 mb-0" class="mb-0 is-required">
                                            <b-form-input v-model.number="item.Density" type="number" step="0.0001" :placeholder="currentDensity ? currentDensity.toString() : ''" :disabled="!item.FromFuelStorageId" @input="calculateRowQuantity(item)" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="4">
                                        <b-form-group label="Kantar Ölçümü [ton]" label-class="font-weight-bold font-small-2 mb-0" class="mb-0 is-required">
                                            <v-validator :errors="errors" :parameter="'FromFuelStorages.' + index + '.Tonnage'" :watch="item.Tonnage">
                                                <v-currency-input :value="item.Tonnage" @update-value="updateTonnage($event, item)" placeholder="Tonaj Giriniz..." :disabled="!item.FromFuelStorageId" />
                                            </v-validator>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="4">
                                        <b-form-group label="Miktar [lt]" label-class="font-weight-bold font-small-2 mb-0" class="mb-0 is-required">
                                            <b-form-input :value="formatNumber(item.TransferQuantity)" disabled class="text-left" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-row class="mt-50">
                                    <b-col md="7">
                                        <b-form-group label="Kantar Fişi" label-class="font-weight-bold font-small-2 mb-0" class="mb-0 is-required">
                                            <v-validator :errors="errors" :parameter="'FromFuelStorages.' + index + '.File'" :watch="item.File">
                                                <input type="file" :ref="'fileInput-' + index" class="d-none" @change="handleFileUpload($event, item)" accept=".pdf,.jpg,.jpeg,.png" />

                                                <div class="d-flex align-items-center">
                                                    <b-button v-if="!item.File" variant="outline-primary" size="sm" class="w-100 d-flex align-items-center justify-content-center" style="height: 38px" @click="$refs['fileInput-' + index][0].click()" :disabled="!item.FromFuelStorageId">
                                                        <feather-icon icon="UploadCloudIcon" class="mr-50" />
                                                        <span class="text-truncate">Fiş Yükle</span>
                                                    </b-button>

                                                    <div v-else class="d-flex align-items-center justify-content-between w-100 border rounded px-1" :class="item.File ? 'border-success bg-light-success' : 'border-primary bg-light'" style="height: 38px">
                                                        <div class="d-flex align-items-center overflow-hidden h-100 py-25">
                                                            <div v-if="item.previewUrl" class="mr-50 border rounded bg-white" style="height: 100%; width: 30px; min-width: 30px; background-size: cover; background-position: center" :style="{ backgroundImage: 'url(' + item.previewUrl + ')' }"></div>
                                                            <feather-icon v-else icon="FileTextIcon" size="20" class="mr-50" :class="item.File ? 'text-success' : 'text-primary'" />
                                                            <span class="text-truncate font-small-3 font-weight-bold" :class="item.File ? 'text-success' : 'text-primary'" :title="item.File.name">{{ item.File.name }}</span>
                                                        </div>
                                                        <div class="cursor-pointer p-50 d-flex align-items-center justify-content-center text-danger hover-danger" @click="removeFile(item)">
                                                            <feather-icon icon="XIcon" size="18" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </v-validator>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="5">
                                        <b-form-group label="Yetkili (Opsiyonel)" label-class="font-weight-bold font-small-2 mb-0" class="mb-0">
                                            <v-select-list v-model="item.FromSupervisorEmployeeId" :options="getSupervisorOptions(item.FromFuelStorageId)" label="name" :reduce="(x) => x.id" placeholder="Seçiniz" :disabled="!item.FromFuelStorageId" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </div>
                        </b-card-body>

                        <b-card-footer class="border-top p-1 mt-50 bg-light-danger">
                            <div class="d-flex justify-content-between align-items-center font-weight-bolder text-danger">
                                <span>TOPLAM ÇIKIŞ:</span>
                                <span>{{ formatNumber(sourceTotal) }} lt</span>
                            </div>
                        </b-card-footer>
                    </b-card>
                </b-col>

                <b-col md="6" class="d-flex">
                    <b-card no-body class="w-100 d-flex flex-column border-success">
                        <b-card-header class="bg-transparent border-bottom py-75 px-1">
                            <div class="d-flex justify-content-between align-items-center w-100">
                                <h5 class="mb-0 text-success"><feather-icon size="20" icon="ArrowDownCircleIcon" /> Hedef (Giriş)</h5>
                                <b-button variant="outline-success" size="sm" class="btn-icon rounded-circle" @click="addDestRow">
                                    <feather-icon icon="PlusIcon" />
                                </b-button>
                            </div>
                        </b-card-header>

                        <b-card-body class="px-1 pt-2 pb-0 flex-grow-1">
                            <div v-for="(item, index) in transferModel.DestinationFuelStorages" :key="'dest-' + index" class="mb-1 p-1 border rounded bg-light">
                                <b-row class="mb-50">
                                    <b-col cols="12">
                                        <b-form-group label="Hedef Şube" label-class="font-weight-bold font-small-2 mb-0" class="mb-0 is-required">
                                            <div class="d-flex">
                                                <div class="flex-grow-1">
                                                    <v-validator :errors="errors" :parameter="'DestinationFuelStorages.' + index + '.TargetBranchId'" :watch="item.TargetBranchId">
                                                        <v-select-list
                                                            v-model="item.TargetBranchId"
                                                            :options="branchesWithFuelStorage"
                                                            label="Name"
                                                            :reduce="(b) => b.Id"
                                                            placeholder="Hedef Şube Seçiniz"
                                                            @input="handleBranchChange($event, item, index)"
                                                        />
                                                    </v-validator>
                                                </div>
                                                <b-button variant="flat-secondary" size="sm" class="btn-icon ml-1" @click="removeDestRow(index)" v-if="transferModel.DestinationFuelStorages.length > 1" style="height: 38px">
                                                    <feather-icon icon="XIcon" />
                                                </b-button>
                                            </div>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-row>
                                    <b-col md="12">
                                        <b-form-group label="Miktar [lt]" label-class="font-weight-bold font-small-2 mb-0" class="mb-0 is-required">
                                            <v-validator :errors="errors" :parameter="'DestinationFuelStorages.' + index + '.IncomingQuantity'" :watch="item.IncomingQuantity">
                                                <v-currency-input :value="item.IncomingQuantity" @update-value="handleDestinationQuantityUpdate($event, item)" placeholder="0,00" :disabled="!item.TargetBranchId || isSingleDestinationAutoFill" />
                                            </v-validator>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </div>
                        </b-card-body>

                        <b-card-footer class="border-top p-1 mt-50 bg-light-success">
                            <div class="d-flex justify-content-between align-items-center font-weight-bolder text-success">
                                <span>TOPLAM GİRİŞ:</span>
                                <span>{{ formatNumber(destTotal) }} lt</span>
                            </div>
                        </b-card-footer>
                    </b-card>
                </b-col>
            </b-row>

            <b-row class="mt-0 pt-0" v-if="hasValidationErrors">
                <b-col cols="12">
                    <b-card no-body class="border-danger mb-0">
                        <b-card-header class="bg-light-danger py-75 px-1">
                            <h6 class="mb-0 text-danger">
                                <feather-icon icon="AlertTriangleIcon" class="mr-50" />
                                Uyarılar
                            </h6>
                        </b-card-header>
                        <b-card-body class="py-75 px-1">
                            <div v-for="(error, idx) in validationErrors" :key="idx" class="d-flex align-items-center py-50" :class="{ 'border-top': idx > 0 }">
                                <feather-icon :icon="error.icon" :class="['mr-75', error.type === 'danger' ? 'text-danger' : 'text-warning']" size="16" />
                                <span :class="error.type === 'danger' ? 'text-danger' : 'text-warning'">{{ error.message }}</span>
                            </div>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>

            <div class="mt-0 pt-2 border-top">
                <b-row>
                    <b-col md="12" class="d-flex justify-content-end align-items-center">
                        <b-button variant="primary" :disabled="isSaveDisabled" @click="save">
                            <b-spinner small v-if="loading" class="mr-50" />
                            <feather-icon v-else icon="SendIcon" class="mr-50" />
                            Transferi Başlat
                        </b-button>
                    </b-col>
                </b-row>
            </div>
        </b-card>
    </b-overlay>
</template>

<script>
import { BCard, BCardFooter, BCardHeader, BCardBody, BRow, BCol, BOverlay, BButton, BFormGroup, BFormInput, BFormSelect, BAlert, BBadge, BSpinner } from "bootstrap-vue";
import FuelStorageApi from "@/api/fuel-storage";
import CompanyApi from "@/api/company";
import EmployeeOperationsApi from "@/api/employee-operations";
import VehicleOperationsApi from "@/api/vehicle-operations";
import VCurrencyInput from "@/components/VCurrencyInput.vue";
import VValidator from "@/components/VValidator.vue";
import VFileUploader from "@/components/v-file-uploader/VFileUploader.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
    name: "FuelStorageTransfer",
    components: {
        BCard,
        BRow,
        BCol,
        BOverlay,
        BButton,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BAlert,
        BCardFooter,
        BCardHeader,
        BCardBody,
        BBadge,
        BSpinner,
        VCurrencyInput,
        VValidator,
        VFileUploader,
    },
    data() {
        return {
            loading: false,
            employeeLoading: false,
            errors: {},
            transferModel: {
                ProcessNumber: "",
                VehiclePlateId: null,
                TrailerPlateId: null,
                CarrierEmployeeId: null,
                FromFuelStorages: [],
                DestinationFuelStorages: [],
            },
            DENSITY_DIESEL: 0.8314,
            DENSITY_ADBLUE: 1.09,
            selectedFuelTypeId: null,
            options: {
                employees: [],
                truckPlates: [],
                trailerPlates: [],
                fuelStorages: [],
                fuelTypes: [],
            },
            storageEmployees: [],
            branches: [],
        };
    },
    mounted() {
        this.generateProcessNumber();
        this.loadOptions();
        this.addSourceRow();
        this.addDestRow();
    },
    computed: {
        currentDensity() {
            if (!this.selectedFuelTypeId) return null;

            if (this.selectedFuelTypeId === "b8694977-e4fc-4c90-b02f-d7bc04caa146") {
                return this.DENSITY_ADBLUE;
            }
            return this.DENSITY_DIESEL;
        },
        sourceTotal() {
            return this.transferModel.FromFuelStorages.reduce((acc, item) => acc + (Number(item.TransferQuantity) || 0), 0);
        },
        destTotal() {
            return this.transferModel.DestinationFuelStorages.reduce((acc, item) => acc + (Number(item.IncomingQuantity) || 0), 0);
        },
        isBalanced() {
            return Math.abs(this.sourceTotal - this.destTotal) < 0.01;
        },
        isSingleDestinationAutoFill() {
            return this.transferModel.DestinationFuelStorages.length === 1;
        },
        filteredFuelStorages() {
            if (!this.selectedFuelTypeId) return [];
            return this.options.fuelStorages
                .filter((s) => s.FuelStorageFuelTypeId === this.selectedFuelTypeId)
                .map((s) => ({
                    ...s,
                    DisplayName: `${s.Name} [${(s.Capacity || 0).toLocaleString("tr-TR")} lt]`,
                }));
        },
        branchesWithFuelStorage() {
            // Hedef şube listesinde sadece yakıt deposu bulunan şubeleri göster.
            // Bir yakıt tipi seçildiyse, o yakıt tipine uygun deposu olan şubelerle daraltıyoruz
            // (Motorin transferi için Adblue-yalnızca-deposu olan şube hedef olmamalı).
            const relevantStorages = (this.options.fuelStorages || []).filter((s) => {
                if (!s.FuelStorageOwningBranchId) return false;
                if (!this.selectedFuelTypeId) return true;
                return s.FuelStorageFuelTypeId === this.selectedFuelTypeId;
            });
            const branchIdsWithStorage = new Set(relevantStorages.map((s) => s.FuelStorageOwningBranchId));
            return (this.branches || []).filter((b) => branchIdsWithStorage.has(b.Id));
        },
        validationErrors() {
            const errors = [];

            this.transferModel.FromFuelStorages.forEach((item, index) => {
                if (item.FromFuelStorageId && item.TransferQuantity > 0) {
                    const storage = this.getStorageInfo(item.FromFuelStorageId);
                    if (storage && item.TransferQuantity > storage.FuelQuantity) {
                        errors.push({
                            type: "danger",
                            icon: "AlertTriangleIcon",
                            message: `Kaynak "${storage.Name}": Depodaki mevcut yakıt (${this.formatNumber(storage.FuelQuantity)} lt) aşıldı!`,
                        });
                    }
                }
            });

            if (this.sourceTotal > 0 && this.destTotal > 0 && !this.isBalanced) {
                const diff = Math.abs(this.sourceTotal - this.destTotal);
                errors.push({
                    type: "warning",
                    icon: "AlertCircleIcon",
                    message: `Çıkış (${this.formatNumber(this.sourceTotal)} lt) ile Giriş (${this.formatNumber(this.destTotal)} lt) eşit değil. Fark: ${this.formatNumber(diff)} lt`,
                });
            }

            return errors;
        },
        hasValidationErrors() {
            return this.validationErrors.length > 0;
        },
        hasCriticalErrors() {
            return this.validationErrors.some((e) => e.type === "danger");
        },
        isFormIncomplete() {
            if (!this.transferModel.ProcessNumber) return true;
            if (!this.selectedFuelTypeId) return true;
            if (!this.transferModel.CarrierEmployeeId) return true;
            if (!this.transferModel.VehiclePlateId) return true;
            if (!this.transferModel.TrailerPlateId) return true;

            const hasValidSource = this.transferModel.FromFuelStorages.some((item) => item.FromFuelStorageId && (item.TransferQuantity || 0) > 0);
            if (!hasValidSource) return true;

            const hasValidDest = this.transferModel.DestinationFuelStorages.some((item) => item.TargetBranchId && (item.IncomingQuantity || 0) > 0);
            if (!hasValidDest) return true;

            return false;
        },
        isSaveDisabled() {
            return this.loading || this.isFormIncomplete || this.hasCriticalErrors;
        },
    },
    methods: {
        loadOptions() {
            this.loading = true;
            Promise.all([this.loadFuelStorages(), this.getTruckPlates(), this.getTrailerPlates(), this.loadFuelTypeOptions(), this.loadFuelStorageEmployees(), this.loadBranches()])
                .catch((err) => {
                    console.error(err);
                    this.$toast({
                        component: ToastificationContent,
                        props: {
                            title: "Hata",
                            icon: "AlertCircleIcon",
                            variant: "danger",
                            text: "Veriler yüklenirken hata oluştu.",
                        },
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadFuelStorages() {
            return FuelStorageApi.FuelStorage.List().then((res) => {
                if (res.data.Success) this.options.fuelStorages = res.data.Data || [];
            });
        },
        loadFuelTypeOptions() {
            this.options.fuelTypes = [
                { text: "Motorin", value: "3a82d544-bfda-4995-ad74-29982d9606df" },
                { text: "AdBlue", value: "b8694977-e4fc-4c90-b02f-d7bc04caa146" },
            ];
            return Promise.resolve();
        },
        loadFuelStorageEmployees() {
            return FuelStorageApi.FuelStorageEmployee.List()
                .then((res) => {
                    if (res.data && res.data.Success) {
                        this.storageEmployees = res.data.Data || [];
                    } else {
                        this.storageEmployees = [];
                    }
                })
                .catch((err) => {
                    console.error(err);
                    this.storageEmployees = [];
                });
        },
        loadBranches() {
            return CompanyApi.Branch.ListByCode("O").then((res) => {
                if (res.data && res.data.Success) {
                    this.branches = res.data.Data || [];
                } else {
                    this.branches = [];
                }
            }).catch((err) => {
                console.error(err);
                this.branches = [];
            });
        },
        searchEmployees(search) {
            if (search) {
                this.employeeLoading = true;
                EmployeeOperationsApi.Employee.SearchActiveDriver(search)
                    .then((res) => {
                        this.options.employees = res.data.Data || [];
                    })
                    .catch((err) => {
                        console.error(err);
                    })
                    .finally(() => {
                        this.employeeLoading = false;
                    });
            }
        },
        getTruckPlates() {
            return VehicleOperationsApi.Plate.ListAll().then((response) => {
                if (response.data.Success) this.options.truckPlates = response.data.Data || [];
            });
        },
        getTrailerPlates() {
            return VehicleOperationsApi.Plate.ListTrailer().then((response) => {
                if (response.data.Success) this.options.trailerPlates = response.data.Data || [];
            });
        },
        addSourceRow() {
            this.transferModel.FromFuelStorages.push({
                FromFuelStorageId: null,
                TransferQuantity: 0,
                Tonnage: null,
                Density: this.currentDensity,
                File: null,
                previewUrl: null,
                FromSupervisorEmployeeId: null,
            });
        },
        updateTonnage(value, item) {
            item.Tonnage = value;
            this.calculateRowQuantity(item);
        },
        calculateRowQuantity(item) {
            const ton = item.Tonnage;
            const density = item.Density;

            if (ton && ton > 0 && density && density > 0) {
                const rawVal = (ton * 1000) / density;
                item.TransferQuantity = parseFloat(rawVal.toFixed(2));
            } else {
                item.TransferQuantity = 0;
            }

            this.syncSingleDestinationQuantity();
        },
        roundToTwo(value) {
            return Number(parseFloat(Number(value || 0).toFixed(2)));
        },
        syncSingleDestinationQuantity() {
            if (!this.isSingleDestinationAutoFill) return;

            const [destination] = this.transferModel.DestinationFuelStorages;
            if (!destination) return;

            destination.IncomingQuantity = destination.TargetBranchId ? this.roundToTwo(this.sourceTotal) : null;
            destination.AutoFilled = !!destination.ToFuelStorageId;
        },
        syncRemainingDestinationQuantity() {
            if (this.isSingleDestinationAutoFill) {
                this.syncSingleDestinationQuantity();
                return;
            }

            const selectedDestinations = this.transferModel.DestinationFuelStorages.filter((item) => item.TargetBranchId);
            if (selectedDestinations.length < 2) return;

            const autoFillCandidates = selectedDestinations.filter((item) => item.AutoFilled || !(Number(item.IncomingQuantity) > 0));
            if (autoFillCandidates.length !== 1) return;

            const [remainingDestination] = autoFillCandidates;
            const allocatedQuantity = selectedDestinations
                .filter((item) => item !== remainingDestination && !item.AutoFilled)
                .reduce((total, item) => total + (Number(item.IncomingQuantity) || 0), 0);

            const remainingQuantity = this.roundToTwo(this.sourceTotal - allocatedQuantity);
            remainingDestination.IncomingQuantity = remainingQuantity > 0 ? remainingQuantity : 0;
            remainingDestination.AutoFilled = true;
        },
        clearDestinationQuantities() {
            this.transferModel.DestinationFuelStorages.forEach((item) => {
                item.IncomingQuantity = null;
                item.AutoFilled = false;
            });
        },
        handleDestinationQuantityUpdate(value, item) {
            item.IncomingQuantity = value;
            item.AutoFilled = false;

            this.$nextTick(() => {
                this.syncRemainingDestinationQuantity();
            });
        },
        handleFileUpload(event, item) {
            const file = event.target.files[0];
            if (file) {
                if (file.size > 5 * 1024 * 1024) {
                    this.$toast({
                        component: ToastificationContent,
                        props: { title: "Uyarı", icon: "AlertTriangleIcon", variant: "warning", text: "Dosya boyutu 5MB'dan büyük olamaz." },
                    });
                    return;
                }
                item.File = file;

                if (file.type.startsWith("image/")) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        this.$set(item, "previewUrl", e.target.result);
                    };
                    reader.readAsDataURL(file);
                } else {
                    this.$set(item, "previewUrl", null);
                }
            }
            event.target.value = null;
        },
        removeFile(item) {
            item.File = null;
            item.previewUrl = null;
        },
        removeSourceRow(index) {
            if (this.transferModel.FromFuelStorages.length > 1) {
                this.transferModel.FromFuelStorages.splice(index, 1);
            }
        },
        addDestRow() {
            this.transferModel.DestinationFuelStorages.push({
                TargetBranchId: null,
                IncomingQuantity: null,
                AutoFilled: false,
            });

            if (this.transferModel.DestinationFuelStorages.length > 1) {
                this.clearDestinationQuantities();
            }
        },
        removeDestRow(index) {
            if (this.transferModel.DestinationFuelStorages.length > 1) {
                this.transferModel.DestinationFuelStorages.splice(index, 1);

                if (this.isSingleDestinationAutoFill) {
                    this.$nextTick(() => {
                        this.syncSingleDestinationQuantity();
                    });
                }
            }
        },
        validate() {
            const newErrors = {};
            let isValid = true;

            if (!this.transferModel.ProcessNumber) {
                newErrors["ProcessNumber"] = "İşlem numarası zorunludur.";
                isValid = false;
            }
            if (!this.selectedFuelTypeId) {
                newErrors["selectedFuelTypeId"] = "Yakıt tipi seçilmelidir.";
                isValid = false;
            }
            if (!this.transferModel.CarrierEmployeeId) {
                newErrors["CarrierEmployeeId"] = "Sürücü seçimi zorunludur.";
                isValid = false;
            }
            if (!this.transferModel.VehiclePlateId) {
                newErrors["VehiclePlateId"] = "Plaka seçimi zorunludur.";
                isValid = false;
            }

            if (!this.transferModel.TrailerPlateId) {
                newErrors["TrailerPlateId"] = "Treyler seçimi zorunludur.";
                isValid = false;
            }

            this.transferModel.FromFuelStorages.forEach((item, index) => {
                if (!item.FromFuelStorageId) {
                    newErrors[`FromFuelStorages.${index}.FromFuelStorageId`] = "Depo seçilmelidir.";
                    isValid = false;
                }
                if ((item.TransferQuantity || 0) <= 0) {
                    newErrors[`FromFuelStorages.${index}.Tonnage`] = "Geçerli bir kantar ölçümü giriniz.";
                    isValid = false;
                }
                if (!item.File) {
                    newErrors[`FromFuelStorages.${index}.File`] = "Kantar fişi yüklenmelidir.";
                    isValid = false;
                }
            });

            this.transferModel.DestinationFuelStorages.forEach((item, index) => {
                if (!item.TargetBranchId) {
                    newErrors[`DestinationFuelStorages.${index}.TargetBranchId`] = "Şube seçilmelidir.";
                    isValid = false;
                }
                if ((item.IncomingQuantity || 0) <= 0) {
                    newErrors[`DestinationFuelStorages.${index}.IncomingQuantity`] = "Miktar giriniz.";
                    isValid = false;
                }
            });

            if (this.hasCriticalErrors) {
                isValid = false;
            }

            this.errors = newErrors;
            return isValid;
        },
        save() {
            if (!this.validate()) return;

            this.loading = true;

            const formData = new FormData();
            formData.append("ProcessNumber", this.transferModel.ProcessNumber);
            if (this.transferModel.VehiclePlateId) formData.append("VehiclePlateId", this.transferModel.VehiclePlateId);
            if (this.transferModel.TrailerPlateId) formData.append("TrailerPlateId", this.transferModel.TrailerPlateId);
            if (this.transferModel.CarrierEmployeeId) formData.append("CarrierEmployeeId", this.transferModel.CarrierEmployeeId);

            this.transferModel.FromFuelStorages.forEach((item, index) => {
                formData.append(`FromFuelStorages[${index}].FromFuelStorageId`, item.FromFuelStorageId);
                formData.append(`FromFuelStorages[${index}].Tonnage`, String(item.Tonnage).replace(".", ","));
                formData.append(`FromFuelStorages[${index}].Density`, String(item.Density).replace(".", ","));
                formData.append(`FromFuelStorages[${index}].TransferQuantity`, String(item.TransferQuantity).replace(".", ","));
                if (item.FromSupervisorEmployeeId) formData.append(`FromFuelStorages[${index}].FromSupervisorEmployeeId`, item.FromSupervisorEmployeeId);
                if (item.File) formData.append(`FromFuelStorages[${index}].File`, item.File);
            });

            this.transferModel.DestinationFuelStorages.forEach((item, index) => {
                if (item.TargetBranchId) formData.append(`DestinationFuelStorages[${index}].TargetBranchId`, item.TargetBranchId);
                formData.append(`DestinationFuelStorages[${index}].IncomingQuantity`, String(item.IncomingQuantity).replace(".", ","));
            });
            FuelStorageApi.FuelStorageTransaction.Transfer(formData)
                .then((res) => {
                    if (res.data.Success) {
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: "Başarılı",
                                icon: "CheckCircleIcon",
                                variant: "success",
                                text: "Transfer süreci başlatıldı. Yetkili onayı bekleniyor.",
                            },
                        });
                        this.resetForm();
                    } else {
                        this.$toast({
                            component: ToastificationContent,
                            props: { title: "Hata", icon: "AlertCircleIcon", variant: "danger", text: res.data.Message || "İşlem başarısız." },
                        });
                    }
                })
                .catch((err) => {
                    console.error(err);
                    this.$toast({
                        component: ToastificationContent,
                        props: { title: "Hata", icon: "AlertCircleIcon", variant: "danger", text: "Sunucu hatası oluştu." },
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getStorageInfo(id) {
            if (!id) return null;
            return this.options.fuelStorages.find((s) => s.Id === id);
        },
        getSupervisorOptions(storageId) {
            const storage = this.getStorageInfo(storageId);
            if (!storage || !storage.Supervisors || storage.Supervisors.length === 0) return [];

            return storage.Supervisors.map((sup) => {
                const empDef = this.storageEmployees.find((e) => e.EmployeeId === sup.EmployeeId);
                return {
                    id: sup.EmployeeId,
                    name: empDef && empDef.FullName ? empDef.FullName : "Bilinmeyen Personel",
                };
            });
        },
        formatNumber(value) {
            if (value === null || value === undefined || isNaN(value)) return "0,00";
            return new Intl.NumberFormat("tr-TR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(value);
        },
        handleBranchChange(val, item, index) {
            item.IncomingQuantity = null;
            item.AutoFilled = false;

            const prefix = `DestinationFuelStorages.${index}`;
            Object.keys(this.errors).forEach((key) => {
                if (key.startsWith(prefix)) {
                    this.$delete(this.errors, key);
                }
            });

            if (this.isSingleDestinationAutoFill) {
                this.syncSingleDestinationQuantity();
            } else {
                this.$nextTick(() => {
                    this.syncRemainingDestinationQuantity();
                });
            }
        },
        handleStorageChange(val, item, type, index) {
            item.TransferQuantity = null;
            item.Tonnage = null;
            item.File = null;
            item.previewUrl = null;
            item.FromSupervisorEmployeeId = null;
            item.AutoFilled = false;

            const prefix = `FromFuelStorages.${index}`;

            Object.keys(this.errors).forEach((key) => {
                if (key.startsWith(prefix)) {
                    this.$delete(this.errors, key);
                }
            });

            this.syncSingleDestinationQuantity();
            this.syncRemainingDestinationQuantity();
        },
        clearIntersectionErrors() {
            // No longer needed as destinations use branches, not storage IDs
        },
        generateProcessNumber() {
            const now = new Date();
            const yyyy = now.getFullYear();
            const mm = String(now.getMonth() + 1).padStart(2, "0");
            const dd = String(now.getDate()).padStart(2, "0");
            const hh = String(now.getHours()).padStart(2, "0");
            const min = String(now.getMinutes()).padStart(2, "0");
            const ms = String(now.getMilliseconds()).padStart(3, "0") + Math.floor(Math.random() * 10);

            this.transferModel.ProcessNumber = `${yyyy}${mm}${dd}${hh}${min}_${ms}`;
        },
        resetForm() {
            this.transferModel = {
                ProcessNumber: "",
                VehiclePlateId: null,
                TrailerPlateId: null,
                CarrierEmployeeId: null,
                FromFuelStorages: [],
                DestinationFuelStorages: [],
            };
            this.selectedFuelTypeId = null;
            this.errors = {};
            this.generateProcessNumber();
            this.addSourceRow();
            this.addDestRow();
        },
    },
    watch: {
        sourceTotal() {
            this.syncSingleDestinationQuantity();
            this.syncRemainingDestinationQuantity();
        },
        "transferModel.DestinationFuelStorages.length"(newLength) {
            if (newLength === 1) {
                this.$nextTick(() => {
                    this.syncSingleDestinationQuantity();
                });
                return;
            }

            this.$nextTick(() => {
                this.syncRemainingDestinationQuantity();
            });
        },
        selectedFuelTypeId(newVal) {
            this.transferModel.FromFuelStorages.forEach((item) => {
                item.FromFuelStorageId = null;
                item.FromSupervisorEmployeeId = null;
                item.Density = this.currentDensity;
                this.calculateRowQuantity(item);
            });
            this.transferModel.DestinationFuelStorages.forEach((item) => {
                item.TargetBranchId = null;
                item.IncomingQuantity = null;
            });
        },
        "transferModel.FromFuelStorages": {
            handler() {
                this.clearIntersectionErrors();
            },
            deep: true,
        },
        "transferModel.DestinationFuelStorages": {
            handler() {
                this.clearIntersectionErrors();
                this.syncRemainingDestinationQuantity();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.border-right {
    border-right: 1px solid #ebe9f1;
}
</style>
<style scoped>
.is-required >>> .col-form-label::after,
.is-required /deep/ .col-form-label::after {
    content: " *";
    color: #dc3545;
    font-weight: 700;
}
</style>
